import { Obj } from "@/types";

/**
 * Pricing Types
 */
export enum PriceType {
    FIXED = 'fixed',
    PERCENTAGE = 'percentage',
    MODIFIER = 'modifier',
}

/**
 * Pricing information for options and products
 */
export interface IPricing {
    price: number;
    priceModifier: number;
}

/**
 * Conditional price entry: price applies when all listed conditions match the current selections
 */
export interface IPriceListCondition {
    categoryId: string;
    optionIds: string[];
}

export interface IPriceListItem {
    conditions: IPriceListCondition[];
    price: number;
    vssPrice?: number;
}

/**
 * Price Condition for dynamic pricing rules
 */
export interface IPriceCondition {
    dogma_pricingruleid: string;
    dogma_name: string;
    // Groups: AND across groups, OR within each group. Takes precedence over flat arrays.
    dogma_trigger_groups?: Array<Array<{
        categoryId: string;
        optionId: string;
        optionName: string;
        optionType: number;
        optionTypeName: string;
    }>>;
    dogma_trigger_optionids?: Array<{
        categoryId: string;
        optionId: string;
        optionName: string;
        optionType: number;
        optionTypeName: string;
    }>; // @deprecated — legacy fallback
    dogma_condition_operator: string; // e.g., ">", "<", ">=", "<=", "==", "exists"
    dogma_condition_value?: string;
    // Groups: AND across groups, OR within each group. Takes precedence over flat arrays.
    dogma_expected_groups?: Array<Array<{
        categoryId: string;
        optionId: string;
        optionName: string;
        optionType: number;
        optionTypeName: string;
    }>>;
    dogma_expected_optionids?: Array<{
        categoryId: string;
        optionId: string;
        optionName: string;
        optionType: number;
        optionTypeName: string;
    }>; // @deprecated — legacy fallback
    dogma_operation: string; // e.g., "add", "multiply"
    dogma_price_value: number;
    dogma_apply_per_metre?: boolean;
    dogma_apply_per_unit?: boolean;
    dogma_apply_per_sash?: boolean;
    resolverValue?: string;
    statecode: number;
    createdon: string;
    modifiedon?: string;
    /** Merged from pricingRuleDiscounts at config-transform time */
    discountPercentage?: number;
    discountAmount?: number;
}

/**
 * VSS discount applied to a specific pricing rule (from pricingRuleDiscounts in config)
 */
export interface IPricingRuleDiscount {
    ruleId: string;
    ruleName: string;
    discountPercentage: number;
    discountAmount: number;
}

/**
 * Surge item representing an applied pricing rule
 */
export interface ISurgeItem {
    ruleId: string;
    ruleName: string;
    description?: string;
    surgeAmount: number;          // dealer price (margin on top of post-discount VSS)
    vssSurgeAmount: number;       // raw VSS price (before discount and margin)
    vssDiscountAmount?: number;   // VSS saving from merged pricingRuleDiscount — tracked separately, never subtracted from surgeAmount/vssSurgeAmount
    operation: string; // "add" or "multiply"
    appliedPerMetre?: boolean;
    applyPerUnit?: boolean;
    applyPerSash?: boolean;
    resolverValue?: string;
    triggerOption?: {
        optionId: string;
        optionName: string;
        categoryName: string;
    };
}

/**
 * Pricing breakdown for display
 */
export interface IPricingBreakdown {
    basePrice: number;
    optionsPrice: number;
    surgeItems: ISurgeItem[];
    totalSurge: number;
    finalPrice: number;
}

export type AlertVariant = 'info' | 'success' | 'warning' | 'error';

export interface IAlertAction {
    label: string;
    actionId: string;
    variant?: 'primary' | 'secondary';
}

export interface IAlertItem {
    label: string;
    statusVariant: AlertVariant;
    detail?: string;
}

export interface IAssistantMessage {
    type?: "user" | "assistant" | "price-surge" | "alert"
    message?: string
    question?: IQuestion,
    response?: {
        answer?: IQuestionOption[],
        selectedOptionsIds?: string[],
        form?: Obj
    }
    id?: string,
    isProcessing?: boolean
    surgeItems?: ISurgeItem[];
    // Alert message fields
    alertVariant?: AlertVariant;
    alertTitle?: string;
    alertActions?: IAlertAction[];
    alertItems?: IAlertItem[];
}

export enum QuestionType {
    TEXT_INPUT = 1,
    NUMBER_INPUT = 2,
    BOOLEAN = 3,
    MULTIPLE_CHOICE = 4,
    SINGLE_SELECT = 5,
    SINGLE_SELECT_WITH_IMAGE = 6,
    SWATCH_SELECT = 7,
    VISUAL_SELECT = 8,
    COLOR_PICKER = 9,
}

export interface IDependency {
    categoryId?: string;
    optionIds?: string[];
}

export interface IDisplayCondition {
    enabled: boolean;
    dependsOn?: IDependency[];
    validForProducts?: string[];
    widthMin?: number; // min frame width (mm) inclusive
    widthMax?: number; // max frame width (mm) inclusive
}

export type IQuestionOption = {
    description: string;
    numberOfFrames: number;
    numberOfSashes?: number;
    optionUnit?: number;
    optionId: string;
    optionText: string;
    optionType: number;
    inputType: number;
    optionTypeName: string;
    order: number;
    responseValue: string;
    thumbnail: string;
    vssPrice: number,
    vssDiscountAmount?: number,
    discountPercentage?: number,
    discountAmount?: number,
    /** Discount used instead of discountPercentage/discountAmount when this
     * option's priceList resolves to its unglazed price entry. */
    unglazedDiscountPercentage?: number,
    unglazedDiscountAmount?: number,
    displayCondition?: IDisplayCondition;
    validProducts?: string[];
    value?: string
    windowJson?: Array<{ bay_side: string; perspective_corners: Array<{ x: number; y: number }> }> | string;
    priceConditions?: IPriceCondition[];
    priceList?: IPriceListItem[];
    priceListDiscounts?: Array<{index: number; discountAmount: number; discountPercentage: number}>;
    producttypecode?: number;
    widthQtyThreshold?: number; // mm threshold: frame >= this value → qty 2, below → qty 1
} & IPricing
export interface IQuestion {
    questionId: string;
    step: number;
    category: string;
    categoryName: string;
    optionType: number;
    optionTypeName: string;
    question: string;
    questionType: QuestionType;
    displayCondition?: IDisplayCondition;
    responsePath?: string;
    options?: IQuestionOption[];
    helperText?: string;
    priceConditions?: IPriceCondition[];
}

export interface IOptionValidationRule {
    dogma_validationruleid: string;
    dogma_name: string;
    dogma_min: number;
    dogma_max: number;
    createdon: string;
    dgma_options: string[];
    modifiedon: string;
    statecode: number;
}

/**
 * Chatbot Configuration
 */
export interface IChatbotConfig {
    greeting?: string;
    language?: string;
    currency?: string;
    units?: string;
    defaultRegion?: string;
}

/**
 * Metadata for configuration
 */
export interface IConfigMetadata {
    generatedAt: string;
    totalCategories: number;
    totalQuestions: number;
}

/**
 * Customer Data
 */
export interface ICustomerData {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    propertyAddress?: string;
    postCode?: string;
    projectType?: string;
    projectReference?: string;
    customerId?: string;
    vatPercentage?: number;
    marginPercentage?: number;
    propertyImage?: {
        imageUrl?: string;
        locations?: Array<{
            x: number;
            y: number;
            width: number;
            height: number;
        }>;
        windows?: Array<{
            id: string;
            label: string;
            x: number;
            y: number;
            width: number;
            height: number;
        }>;
    };
}

/**
 * Cart Item for storing window configurations
 */
export interface IWindowCartItem {
    id: string;
    windowName: string;
    messages: IAssistantMessage[];
    price: IPriceBreakdownList;
    /** Primary window — always present, used for single-window and as the first window for multi-window blends */
    window: {
        id: string;
        label: string;
        x: number;
        y: number;
        width: number;
        height: number;
    }
    /**
     * All blended window boxes — populated when a duplicate was created with multiple windows blended at once.
     * When present, `window` is the first entry. Use this array for status checks to correctly identify
     * which windows are covered by this cart item.
     */
    windows?: Array<{
        id: string;
        label: string;
        x: number;
        y: number;
        width: number;
        height: number;
    }>;
    quantity: number;
    timestamp: number;
    images?: string[];
    points?: any[]
    propertyImageUrl?: string;
    blendedImageUrl?: string;
    onlineBlendedImageUrl?: string;
    windowAnnotationId?: string; // Tracks which window annotation (by ID) this cart item corresponds to
    /** Fire egress consent captured at the time of adding to cart. */
    fireEgressConsent?: 'accepted' | 'rejected';
}

/**
 * Complete Cart with customer info
 */
export interface IAssistantCart {
    customer: ICustomerData;
    windowConfiguration: IWindowCartItem[];
    vssDiscountPercentage?: number;
    customDiscountPercentage?: number;
    customDiscountAmount?: number;
    discountType?: 'percentage' | 'amount'; // Type of custom discount being applied
}

/**
 * Complete Assistant Configuration
 */
export interface IDiscountConfigOption {
    optionId: string;
    optionText: string;
    discountPercentage: number;
    discountAmount: number;
    priceListDiscounts?: Array<{index: number; discountAmount: number; discountPercentage: number}>;
}

export interface IDiscountConfigQuestion {
    questionId: string;
    question: string;
    options: IDiscountConfigOption[];
}

export interface IDiscountConfig {
    type: string;
    discountPercentage: number;
    discountAmount: number;
    freeDelivery: boolean;
    excludingWindowProduct?: boolean;
    questionFlow: IDiscountConfigQuestion[];
}

export interface IAssistantConfig {
    version: string;
    configName: string;
    lastUpdated: string;
    brandName?: string;
    productLine?: string;
    chatbotConfig: IChatbotConfig;
    questionFlow: IQuestion[];
    validationRules?: IOptionValidationRule[];
    pricingRules?: IPriceCondition[];
    pricingRuleDiscounts?: IPricingRuleDiscount[];
    metadata?: IConfigMetadata;
    discountConfig?: IDiscountConfig;
}


export interface IPriceBreakdown {
    price: number,
    vssPrice: number,
    vssDiscountAmount?: number,
    label: string,
    optionName: string,
    categoryName: string,
    optionId?: string,
    /** Per-item unit multiplier: numberOfFrames when "same for all" is active,
     *  entered numeric value for priced form fields, 1 otherwise. */
    unit?: number,
    /** When true this is a flat one-off charge that must NOT be multiplied by cart qty. */
    flatCharge?: boolean,
    resolverValue?: string,
}
export interface IPriceBreakdownList {
    breakdown: IPriceBreakdown[],
    totalPrice: number,
    basePrice: number,
    surchargePrice: number,
    totalOptionPrice: number,
    currency: string
    vssBasePrice?: number
    vssTotalPrice?: number
    /** Sum of all flat surcharges that must not be scaled by cart qty. */
    flatChargeTotal?: number
    /** VSS equivalent of flatChargeTotal — must not be scaled by cart qty. */
    vssFlatChargeTotal?: number
    /** Total dealer savings on VSS across all breakdown items (account discountConfig) */
    totalVssDiscountAmount?: number
    /** Total VSS savings from pricingRuleDiscounts on surge/pricing-rule charges */
    totalSurgeVssDiscountAmount?: number
}

export interface IConfigBreakDown {
    categoryName: string,
    message: IAssistantMessage
    windowName?: string
    /** Number of window frames this item applies to.
     *  = numberOfFrames (from selected variation/window type) when "same for all" is chosen,
     *  = the entered numeric value for priced form fields,
     *  = 1 otherwise. */
    unit: number
    /** Price per unit for priced form-field options (priceModifier / price). */
    price?: number
}

export interface IConfigurationBreakdownDetail {
    categoryName: string;
    details: IConfigBreakDown[];
}

export interface WindowAnnotation {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
}


export interface ICustomerInformationForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  propertyAddress: string;
  postCode: string;
  projectType: string;
  projectReference?: string;
  vatPercentage?: number;
  marginPercentage?: number;
  propertyImage?: {
    imageUrl?: string;
    locations?: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
    }>;
    windows?: WindowAnnotation[];
  };
}

export interface IQuoteFormData {
  customerInformation?: ICustomerInformationForm;
  customerId?: string;
  // Add more steps data here as needed
  // windowConfiguration?: IWindowConfigurationForm;
  // quoteSummary?: IQuoteSummaryForm;
}